import Keycloak, {KeycloakInitOptions} from 'keycloak-js';
import configuration from "@/configuration/configuration";
import { PageName } from '@/configuration/pages';
import { RoleName } from '@/configuration/roles';
import { UserDetailsAccreditation } from '@/model/user/UserDetailsAccreditation';
import {storesApiClient} from "@/configuration/api-client";
import {Drive} from "@/model/Drive";

export const SESSION_STORAGE_USER_RIGHTS_KEY = 'userRights';
export const NO_RIGHTS_ERROR = 'User has no rights on the application.';
const REGEXP_MOBILE_USER_AGENT = (window as any).userAgentRegExp;

const keycloak = new Keycloak({
    url: configuration.keycloakUrl,
    realm: configuration.keycloakRealm,
    clientId: configuration.keycloakClientId,
});

export function init(): Promise<void> {
    return new Promise((resolve, reject) => {
        const initOptions: KeycloakInitOptions = {onLoad: 'login-required','checkLoginIframe' : false};
        keycloak.init(initOptions)
            .then((authenticated) => {
                resolve();
            })
            .catch((error) => {
                reject(error);
            });
    });
}

export interface UserDetails {
    email: string;
    name: string;
    site: SiteDetails;
}

export interface SiteDetails {
    id: string;
    label: string;
}

export interface UserRights {
    role: RoleName;
    zonesAccreditations?: UserDetailsAccreditation[];
    overrideStoreId: string;
}

export default {
    undetermined: "N/A",

    isAuthorizedZone(zone: string): boolean {
        const userRights = this.userRights();
        return userRights &&
                ((userRights.role !== 'USER' && userRights.role !== 'MANAGER') || userRights.zonesAccreditations.some(x => x.zone === zone));
    },
    canAccess(pageName: PageName): boolean {
        const userRights = this.userRights();
        const role: RoleName = userRights && userRights.role;

        switch (pageName) {
            case PageName.STORE:
                return [RoleName.CENTRAL_ADMIN].includes(role);
            case PageName.STORES:
                return [RoleName.CENTRAL_ADMIN].includes(role);
            case PageName.MASS_IMPORT:
                return [RoleName.CENTRAL_ADMIN].includes(role) && !this.isMobileUser();
            case PageName.CLOSURES:
                return [RoleName.CENTRAL_ADMIN, RoleName.STORE_ADMIN].includes(role);
            case PageName.ALERT:
            case PageName.HISTORY:
            case PageName.USERS:
                return [RoleName.CENTRAL_ADMIN, RoleName.STORE_ADMIN, RoleName.MANAGER].includes(role);
            case PageName.COLLECTION_SLOTS:
                return [RoleName.CENTRAL_ADMIN, RoleName.STORE_ADMIN, RoleName.MANAGER, RoleName.DRIVE_USER, RoleName.USER].includes(role);
            case PageName.DROP_OFFS:
                return [RoleName.CENTRAL_ADMIN, RoleName.STORE_ADMIN, RoleName.MANAGER, RoleName.DRIVE_USER, RoleName.USER].includes(role) && this.canHandleSupports();
            case PageName.ORDERS:
            case PageName.PREPARATION:
                return [RoleName.CENTRAL_ADMIN, RoleName.STORE_ADMIN, RoleName.MANAGER, RoleName.DRIVE_USER, RoleName.USER].includes(role);
            case PageName.PRODUCTS:
                return [RoleName.CENTRAL_ADMIN, RoleName.STORE_ADMIN, RoleName.MANAGER].includes(role);
            default:
                return false;
        }
    },
    canHandleSupports(): boolean {
        const storeDrives = sessionStorage.getItem("storeDrives");
        if (storeDrives != null && storeDrives.length > 0) {
            const drive: Drive = (JSON.parse(storeDrives) as Drive[]).at(0);
            return drive.wms === "MAWM";
        }
    },
    isMobileUser(): boolean{
        if(REGEXP_MOBILE_USER_AGENT == null){
            return true;
        }
        return REGEXP_MOBILE_USER_AGENT.test(navigator.userAgent);
    },
    userDetails(): UserDetails {
        if (keycloak.idTokenParsed && keycloak.tokenParsed) {
            return {
                // @ts-ignore
                email: keycloak.idTokenParsed.email,
                // @ts-ignore
                name: keycloak.idTokenParsed.name,
                // @ts-ignore
                site: this.siteDetails(keycloak.tokenParsed.iir_mgt_site),
            };
        }
        this.logout();
    },
    userRights(): UserRights {
        const userRightsJson = sessionStorage.getItem(SESSION_STORAGE_USER_RIGHTS_KEY);
        
        if (!userRightsJson) {
            console.error(NO_RIGHTS_ERROR);
            this.logout();
            return null;
        }

        const userRights = JSON.parse(userRightsJson);
        if (!userRights || typeof userRights !== 'object') {
            console.error(NO_RIGHTS_ERROR);
            this.logout();
            return null;
        }

        return userRights;
    },
    siteDetails(tokenParam: string): SiteDetails {
        const naStore = {
            id: this.undetermined,
            label: this.undetermined
        };

        if (tokenParam == null) {
            return naStore;
        }

        const details: string[] = tokenParam.split("|");
        const siteWithoutFra = details[0].replace("FRA", "");
        
        if (details == null || details.length == 0) {
            return naStore;
        }

        return {
            id: siteWithoutFra,
            label: details[1]
        }
    },
    logout: function() {
        sessionStorage.clear();
        keycloak.logout({redirectUri: window.location.origin});
    },
    isCentralAdmin(): boolean {
        return this.userHasRoles([RoleName.CENTRAL_ADMIN]);
    },
    isDriveUser(): boolean {
        return this.userHasRoles([RoleName.DRIVE_USER]);
    },
    userHasRoles(roles: RoleName[]): boolean {
        return roles?.includes(this.userRights()?.role) ?? false;
    },
    ensureFreshToken(): Promise<string> {
        return new Promise((resolve, reject) => {
            keycloak
                .updateToken(10)
                .then(() => resolve(keycloak.token))
                .catch(() => reject());
        });
    },
    loadUserRights(): Promise<UserRights> {
        return storesApiClient.get('/users')
            .then((response: any) => {
                const userRights: UserRights = response && response.data ? response.data : null;
                if(!userRights || ((userRights.zonesAccreditations == null || userRights.zonesAccreditations.length == 0) && userRights.role !== RoleName.DRIVE_USER)){
                    return Promise.reject(NO_RIGHTS_ERROR);
                }
                sessionStorage.setItem(SESSION_STORAGE_USER_RIGHTS_KEY, JSON.stringify(userRights));
                return Promise.resolve(userRights)
            })
            .catch(error => {
                if(error.response && error.response.status === 404){
                    return Promise.reject(NO_RIGHTS_ERROR);
                }
                sessionStorage.removeItem(SESSION_STORAGE_USER_RIGHTS_KEY);
                return Promise.reject(error);
            });
    }
};
